import { designVariables } from "@studio3t/design-variables";
import styled from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ConfigItem, ConfigKey, ConfigVal } from "@/components/SectionCard";
import { SkeletonBar } from "@/components/Skeleton";
import type { Checkpoint, CursorInfo } from "@/types/api";
import { checkpointAge, currentCheckpoints } from "./checkpointView";
import { FULL_LOGS_HEIGHT, TabEmptyState } from "./styles";

interface CheckpointsSectionProps {
  checkpoints: Checkpoint[] | null | undefined;
  // True while the checkpoints query is in flight. Distinguishes "still
  // loading" (skeleton) from "loaded, none recorded" (empty state).
  loading: boolean;
}

const SEMANTIC = designVariables.ui.semantic;

// Card body per Figma 13495-42671: label/value rows (reusing the Configuration
// card's ConfigItem/Key/Val tokens). A job reports one checkpoint per source
// partition, so the list can grow long — cap it at 500px and scroll vertically
// with the app's custom scrollbars (OverlayScroll, like the tables), keeping the
// card header fixed above. OverlayScrollbars manages the overflow.
const Scroll = styled(OverlayScroll)`
  max-height: 500px;
`;

// Inner padded content so the scrollbar overlays at the card edge rather than
// inside the body padding.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacings}px;
  padding: 0 ${SEMANTIC.spacingm}px ${SEMANTIC.spacings}px;
`;

// One checkpoint = three rows. When a job reports more than one partition, a 1px
// rule separates the groups (the Figma shows a single checkpoint).
const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacings}px;

  &:not(:first-child) {
    padding-top: ${SEMANTIC.spacings}px;
    border-top: 1px solid ${(props) => props.theme.tableBackground};
  }
`;

// A job can report one checkpoint per source partition, so job_name alone is
// not unique. Kafka has a naturally stable+unique key (topic + partition), so
// it needs no index — keeping the key independent of array order. MongoDB's
// resume token can be empty/identical across partitions at cold start, so it
// keeps the index as a tiebreaker (likewise the unknown-cursor fallback).
function checkpointKey(cp: Checkpoint, index: number): string {
  const c = cp.cursor;
  switch (c.source) {
    case "kafka":
      return `${cp.job_name}:kafka:${c.topic}:${c.partition}`;
    case "mongodb": {
      const token =
        typeof c.data._data === "string"
          ? c.data._data
          : JSON.stringify(c.data);
      return `${cp.job_name}:mongodb:${token}:${index}`;
    }
    default:
      return `${cp.job_name}:${c.source}:${index}`;
  }
}

function truncated(text: string, length: number): string {
  return text.length > length ? `${text.substring(0, length)}...` : text;
}

// A change stream's position: its resume token, or failing that the first
// field the cursor carries.
function mongoCursorDetails(data: Record<string, unknown>): string {
  if (data._data && typeof data._data === "string") {
    return `Resume token: ${truncated(data._data, 24)}`;
  }
  const [firstKey] = Object.keys(data);
  if (firstKey === undefined) return "Cursor data available";
  return `${firstKey}: ${truncated(JSON.stringify(data[firstKey]), 30)}`;
}

function formatCursorInfo(cursor: CursorInfo): {
  type: string;
  details: string;
} {
  switch (cursor.source) {
    case "kafka":
      return {
        type: "Kafka",
        details: `${cursor.topic}[${cursor.partition}]:${cursor.offset}`,
      };
    case "mongodb":
      return { type: "MongoDB", details: mongoCursorDetails(cursor.data) };
    case "postgres_cdc":
      return {
        type: "PostgreSQL change capture",
        details: `Handed off through LSN ${cursor.lsn}`,
      };
    case "unknown":
      return {
        type: "Unknown",
        details: `${cursor.raw_bytes} bytes`,
      };
    default:
      return { type: "Unknown", details: "—" };
  }
}

// Shape of one checkpoint group while loading: three label/value row pairs,
// like a real checkpoint (Last updated / Cursor Type / Cursor Position).
const SKELETON_ROWS = ["updated", "type", "position"];

// Two skeleton groups (a multi-partition checkpoint list) with the real Group
// separator between them, in a Body stretched to the shared tab height — the
// same space the empty state and Logs card reserve — so the skeleton covers
// the whole panel and the swap to data doesn't change the card's height.
const SKELETON_GROUPS = ["group-1", "group-2"];

const SkeletonBody = styled(Body)`
  min-height: ${FULL_LOGS_HEIGHT}px;
  justify-content: space-evenly;
`;

function CheckpointsSkeleton() {
  return (
    <Scroll aria-hidden="true">
      <SkeletonBody>
        {SKELETON_GROUPS.map((group) => (
          <Group key={group}>
            {SKELETON_ROWS.map((key) => (
              <ConfigItem key={key}>
                <ConfigKey>
                  <SkeletonBar $width="96px" />
                </ConfigKey>
                <ConfigVal>
                  {/* Fixed width: ConfigVal shrink-wraps its content, so a
                      percentage would resolve against zero and collapse. */}
                  <SkeletonBar $width="220px" />
                </ConfigVal>
              </ConfigItem>
            ))}
          </Group>
        ))}
      </SkeletonBody>
    </Scroll>
  );
}

// The Checkpoints tab body. Unlike the old card (which hid itself when empty),
// the tab is always present, so render an empty state when there are none — but
// only once the query has resolved; a fetch in flight shows a skeleton instead.
export function CheckpointsBody({
  checkpoints,
  loading,
}: CheckpointsSectionProps) {
  if (loading && !checkpoints) {
    return <CheckpointsSkeleton />;
  }
  if (!checkpoints || checkpoints.length === 0) {
    return (
      <TabEmptyState>No checkpoints recorded for this pipeline</TabEmptyState>
    );
  }

  // One position per source partition; the older checkpoints the engine
  // keeps are history, counted rather than listed.
  const { current, earlier } = currentCheckpoints(checkpoints);
  const now = Date.now();
  return (
    <Scroll>
      <Body>
        {current.map((cp, index) => {
          const cursorInfo = formatCursorInfo(cp.cursor);
          const age = checkpointAge(cp.updated_at, now);
          return (
            <Group key={checkpointKey(cp, index)}>
              <ConfigItem>
                <ConfigKey>Last updated</ConfigKey>
                <ConfigVal>
                  {cp.updated_at}
                  {age && <Age> · {age}</Age>}
                </ConfigVal>
              </ConfigItem>
              <ConfigItem>
                <ConfigKey>Cursor Type</ConfigKey>
                <ConfigVal>{cursorInfo.type}</ConfigVal>
              </ConfigItem>
              <ConfigItem>
                <ConfigKey>Cursor Position</ConfigKey>
                <ConfigVal>{cursorInfo.details}</ConfigVal>
              </ConfigItem>
            </Group>
          );
        })}
        {earlier > 0 && (
          <Earlier>
            {earlier} earlier {earlier === 1 ? "checkpoint" : "checkpoints"}{" "}
            kept
          </Earlier>
        )}
      </Body>
    </Scroll>
  );
}

const Age = styled.span`
  opacity: 0.7;
`;

const Earlier = styled.div`
  opacity: 0.7;
  font-size: 13px;
`;
